import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Image, Button, message, Typography } from 'antd';
import { PictureOutlined, FontSizeOutlined, PlayCircleFilled, LoadingOutlined, ScanOutlined, DeleteOutlined, ExpandAltOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import api from '../../../api/client';
import { useParams } from 'react-router-dom';
import { useGeneration } from '../../../context/GenerationContext';

const { Text } = Typography;

const SegmentationNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const appEdges = getEdges();
            const appNodes = getNodes();

            // 1. Get Prompt (Left Top)
            const promptEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-top');
            let prompt = "";
            if (promptEdge) {
                const sourceNode = appNodes.find(n => n.id === promptEdge.source);
                if (sourceNode?.data?.text) {
                    prompt = sourceNode.data.text;
                }
            }

            if (!prompt) {
                message.warning('Please connect a Text Node (with object name) to the top-left handle.');
                setGenerating(false);
                return;
            }

            // 2. Get Source Image (Left Bottom)
            const imageEdge = appEdges.find(e => e.target === id && e.targetHandle === 'left-bottom');
            let sourceImageUrl = null;
            let sourceWidth = 1024;
            let sourceHeight = 1024;

            if (imageEdge) {
                const sourceNode = appNodes.find(n => n.id === imageEdge.source);
                if (sourceNode?.data?.src) {
                    sourceImageUrl = sourceNode.data.src;
                    // Try to get dimensions from source node data
                    if (sourceNode.data.width) sourceWidth = sourceNode.data.width;
                    if (sourceNode.data.height) sourceHeight = sourceNode.data.height;
                    // Check metadata if it's a generation result
                    if (sourceNode.data.metadata?.width) sourceWidth = sourceNode.data.metadata.width;
                    if (sourceNode.data.metadata?.height) sourceHeight = sourceNode.data.metadata.height;
                }
            }

            if (!sourceImageUrl) {
                message.warning('Please connect an Image Node to the bottom-left handle.');
                setGenerating(false);
                return;
            }

            // Call API - preview: true is required to get the actual mask image
            const response = await api.post('/fal/segmentation', {
                image_url: sourceImageUrl,
                object: prompt,
                preview: true
            });

            const { request_id } = response.data;
            const modelId = 'fal-ai/moondream3-preview/segment';

            // setGenerating(false); // REMOVED

            trackGeneration(request_id, modelId, {
                prompt: 'Segmentation',
                type: 'Mask',
                canvas_id: canvasId,
                node_id: id,
                promptProp: `Segment: ${prompt}`,
                width: sourceWidth,
                height: sourceHeight
            }, {
                onComplete: async (result) => {
                    if (result && result.image && result.image.url) {
                        updateData('src', result.image.url);
                        message.success('Segmentation mask generated!');
                    } else {
                        message.error('Failed to generate segmentation mask.');
                        console.error('Unexpected result:', result);
                    }
                    setGenerating(false);
                },
                onFail: (err) => {
                    console.error("Segmentation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Segmentation error:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            setGenerating(false); // Stop glow if submission fails
        }
    };

    const clearImage = (e) => {
        e.stopPropagation();
        updateData('src', null);
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={300}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Segmentation'}
                iconType={data.iconType || 'scan'}
                color={data.nodeColor || '#13c2c2'}
                onTitleChange={onTitleChange}
            />

            {/* Input Handles */}
            <Handle
                type="target"
                position={Position.Left}
                id="left-top"
                style={{
                    ...getHandleStyle(TYPE_COLORS.text, 'left', handlesVisible),
                    top: 40
                }}
            />

            <Handle
                type="target"
                position={Position.Left}
                id="left-bottom"
                style={{
                    ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible),
                    bottom: 40,
                    top: 'auto'
                }}
            />

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)}
            />

            {/* Generate Button */}
            <Button
                type="primary"
                shape="circle"
                icon={generating ? <LoadingOutlined style={{ fontSize: 64 }} /> : <PlayCircleFilled style={{ fontSize: 64 }} />}
                onClick={handleGenerate}
                style={{
                    position: 'absolute',
                    right: -160,
                    bottom: 0,
                    width: 130,
                    height: 130,
                    zIndex: 100,
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                    border: '10px solid #161b22',
                    background: '#13c2c2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: '#161b22' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #13c2c2' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #13c2c2, 0 0 30px rgba(82, 196, 26, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: 8,
                    overflow: 'hidden',
                    animation: generating ? 'pulse-glow 2s infinite' : 'none'
                }}
            >
                <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.src ? (
                        <div
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                            onMouseEnter={(e) => {
                                const overlay = e.currentTarget.querySelector('.preview-overlay');
                                if (overlay) overlay.style.opacity = 1;
                            }}
                            onMouseLeave={(e) => {
                                const overlay = e.currentTarget.querySelector('.preview-overlay');
                                if (overlay) overlay.style.opacity = 0;
                            }}
                        >
                            <Image
                                src={data.src}
                                alt="mask"
                                style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                                preview={{ visible: previewVisible, onVisibleChange: (vis) => setPreviewVisible(vis) }}
                            />

                            <div
                                className="nodrag delete-btn"
                                onClick={clearImage}
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 24,
                                    height: 24,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    zIndex: 10
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                            </div>

                            <div
                                className="preview-overlay nodrag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewVisible(true);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer'
                                }}
                            >
                                <ExpandAltOutlined style={{ fontSize: 24, color: '#fff' }} />
                            </div>
                            <style>{`
                                .preview-overlay:hover ~ .delete-btn,
                                .delete-btn:hover {
                                    opacity: 1 !important;
                                }
                            `}</style>
                        </div>
                    ) : (
                        <div style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#8b949e',
                            padding: 20,
                            textAlign: 'center'
                        }}>
                            <ScanOutlined style={{ fontSize: 48, marginBottom: 16, color: '#13c2c2' }} />
                            <Text style={{ color: '#8b949e', fontSize: 14 }}>Connect Image & Object Text</Text>
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                                Top Left: Object Name<br />
                                Bottom Left: Image Source
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            <style>{`
                @keyframes pulse-glow {
                    0% {
                        box-shadow: 0 0 15px #13c2c2, 0 0 30px rgba(82, 196, 26, 0.4);
                        border-color: #13c2c2;
                    }
                    50% {
                        box-shadow: 0 0 25px #36cfc9, 0 0 50px rgba(82, 196, 26, 0.6);
                        border-color: #36cfc9;
                    }
                    100% {
                        box-shadow: 0 0 15px #13c2c2, 0 0 30px rgba(82, 196, 26, 0.4);
                        border-color: #13c2c2;
                    }
                }
            `}</style>
        </div>
    );
};

export default memo(SegmentationNode);
